import { ElementType, MouseEvent as ReactMouseEvent, ReactElement, memo, useEffect } from 'react';
import { Box, Flex, Tooltip } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import DashboardIcon from 'components/Icons/DashboardIcon';
import DashboardIconActive from 'components/Icons/DashboardIconActive';
import DimensionsIcon from 'components/Icons/DimensionsIcon';
import DimensionsIconActive from 'components/Icons/DimensionsIconActive';
import ImportDataIcon from 'components/Icons/ImportDataIcon';
import ImportDataIconActive from 'components/Icons/ImportDataIconActive';
import { FetchFormulaList, getFormulaList } from 'redux/PlannerModeSlice';
import { FetchDimensionList, handleBuilderSelectedDimension } from 'redux/BuilderModeSlice';
import { selectedFileId } from 'redux/DataImportSlice';
import { getDashboard, setSelectedPage } from 'redux/DashboardSliceV2';
import { Flags } from 'models/Global.interface';
import useDashboardData from 'pages/ModelOverviewPage/Dashboard-v2/hooks/useDashboardData';
import { radius } from 'styles/revampTokens';
import { isUiRevampEnabled } from 'utils/uiRevampFlag';
import { isBlockDimDropdownEnabled, isDataImportDropdownEnabled } from 'utils/navDropdownFlag';

import { getUrlValueFromTab } from '../constants';
import { TabType } from '../types';

import BlocksIconActive from './icons/BlocksIconActive';
import BlocksIcon from './icons/BlocksIcon';
import NavigationArrowDownIcon from './NavigationArrowDownIcon';

interface NavigationBarProperties {
  activeTab: TabType;
  onTabChange: (tab: TabType) => void;
  flags?: Flags;
}

interface NavigationIconProperties {
  width?: number;
  height?: number;
}

interface NavigationTab {
  key: TabType;
  label: string;
  icon: (properties: NavigationIconProperties) => ReactElement;
  activeIcon: (properties: NavigationIconProperties) => ReactElement;
}

interface NavigationDropdownSection {
  id: string | number;
  title: string;
  source?: any;
}

const ACTIVE_TAB_COLOR = '#8B5CF6';
const DROPDOWN_ROW_HOVER_STYLE = { bg: '#F5F3FF', color: ACTIVE_TAB_COLOR };

const tabs: NavigationTab[] = [
  {
    key: 'dashboard',
    label: 'Dashboard',
    icon: DashboardIcon,
    activeIcon: DashboardIconActive,
  },
  {
    key: 'blocks',
    label: 'Blocks',
    icon: BlocksIcon,
    activeIcon: BlocksIconActive,
  },
  {
    key: 'dimensions',
    label: 'Dimensions',
    icon: DimensionsIcon,
    activeIcon: DimensionsIconActive,
  },
  {
    key: 'import',
    label: 'Data',
    icon: ImportDataIcon,
    activeIcon: ImportDataIconActive,
  },
];

const NavigationFlex = Flex as ElementType;
const NavigationBox = Box as ElementType;
const NavigationTooltip = Tooltip as ElementType;
const MotionBox = motion(Box);

const toDropdownSections = (
  fallbackIdPrefix: string,
  items: any[],
  getTitle: (item: any) => string | undefined,
): NavigationDropdownSection[] => {
  return (items || [])
    .map((item: any, index: number) => ({
      id: item?.id ?? `${fallbackIdPrefix}-${index}`,
      title: getTitle(item) || '',
      source: item,
    }))
    .filter((section) => Boolean(section.title));
};

const sortDashboardPages = (pages: any[] | undefined) =>
  [...(pages || [])].sort((a, b) => (a?.position ?? 0) - (b?.position ?? 0));

const getModelTabUrl = (modelId: string | number, tabKey: TabType) =>
  `/model/${modelId}/${getUrlValueFromTab(tabKey)}`;

const saveSelectedDashboardPage = (
  modelId: string | number,
  pageId: string | number | undefined,
) => {
  try {
    const planFilters = localStorage.getItem('global-modal-filters');
    const data = planFilters ? JSON.parse(planFilters) : {};
    data[Number(modelId)] = {
      ...data[Number(modelId)],
      selectedPageId: pageId,
    };
    localStorage.setItem('global-modal-filters', JSON.stringify(data));
  } catch {
    // Ignore localStorage errors (private mode, quota, etc.)
  }
};

const buildDropdownSections = (
  tabKey: TabType,
  formulaList: any,
  dashboardPages: any[] | undefined,
  uiRevamp: boolean,
): NavigationDropdownSection[] => {
  switch (tabKey) {
    case 'blocks':
      return toDropdownSections('block', formulaList?.blocks, (block) => block?.name);
    case 'dimensions':
      return toDropdownSections(
        'dimension',
        formulaList?.dimensions || formulaList?.dimension,
        (dimension) => dimension?.name,
      );
    case 'import':
      return toDropdownSections(
        'data-source',
        formulaList?.data_source || formulaList?.data_sources,
        (dataSource) => dataSource?.file_name,
      );
    case 'dashboard':
      if (!uiRevamp) {
        return [];
      }
      return toDropdownSections('page', sortDashboardPages(dashboardPages), (page) => page?.name);
    default:
      return [];
  }
};

const isDropdownEnabledForTab = (tabKey: TabType, flags?: Flags): boolean => {
  if (tabKey === 'dashboard') {
    return isUiRevampEnabled(flags);
  }
  if (tabKey === 'blocks' || tabKey === 'dimensions') {
    return isBlockDimDropdownEnabled(flags as { blockDimDropdown?: boolean } | undefined);
  }
  if (tabKey === 'import') {
    return isDataImportDropdownEnabled(flags as { dataImportDropdown?: boolean } | undefined);
  }
  return false;
};

const buildDropdownSectionUrl = (
  tabKey: TabType,
  section: NavigationDropdownSection,
  modelId?: string,
): string | null => {
  if (!modelId) {
    return null;
  }

  const encodedId = encodeURIComponent(String(section.id));
  if (tabKey === 'blocks') {
    return `/model/${modelId}/block/${encodedId}/plan`;
  }
  if (tabKey === 'dimensions') {
    return `${getModelTabUrl(modelId, 'dimensions')}?dimensionId=${encodedId}`;
  }
  if (tabKey === 'import') {
    return `${getModelTabUrl(modelId, 'import')}?fileId=${encodedId}&fileName=${encodeURIComponent(
      section.title,
    )}`;
  }
  if (tabKey === 'dashboard') {
    return `${getModelTabUrl(modelId, 'dashboard')}?pageId=${encodedId}`;
  }
  return null;
};

const openUrlInNewTab = (url: string) => {
  const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
  if (newWindow) {
    newWindow.opener = null;
  }
};

const NavigationBar = memo(
  ({ activeTab, onTabChange, flags }: NavigationBarProperties): ReactElement => {
    const { modelId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch: any = useDispatch();
    const formulaList = useSelector(getFormulaList);
    const dashboard = useSelector(getDashboard);
    const uiRevamp = isUiRevampEnabled(flags);
    const blockDimDropdownOn = isBlockDimDropdownEnabled(
      flags as { blockDimDropdown?: boolean } | undefined,
    );
    const dataImportDropdownOn = isDataImportDropdownEnabled(
      flags as { dataImportDropdown?: boolean } | undefined,
    );
    const hasEnabledDropdown = Boolean(blockDimDropdownOn || dataImportDropdownOn || uiRevamp);
    useDashboardData({ enable: uiRevamp });
    useEffect(() => {
      if (modelId && hasEnabledDropdown) {
        dispatch(FetchFormulaList(Number(modelId)));
      }
    }, [dispatch, hasEnabledDropdown, modelId]);

    useEffect(() => {
      const searchParameters = new URLSearchParams(location.search);
      const dimensionId = searchParameters.get('dimensionId');
      const fileId = searchParameters.get('fileId');
      const pageId = searchParameters.get('pageId');

      if (pageId && uiRevamp && modelId) {
        const matchedPage =
          (dashboard?.pages || []).find((p: any) => String(p?.id) === pageId) ?? null;
        if (matchedPage) {
          saveSelectedDashboardPage(modelId, matchedPage.id);
          dispatch(setSelectedPage(matchedPage));
        }
      }

      if (dimensionId && blockDimDropdownOn) {
        const selectedDimension =
          (formulaList?.dimensions || formulaList?.dimension || []).find(
            (dimension: any) => String(dimension?.id) === dimensionId,
          ) ?? null;
        if (selectedDimension) {
          dispatch(handleBuilderSelectedDimension(selectedDimension));
        }
      }

      if (fileId && dataImportDropdownOn) {
        const numericFileId = Number(fileId);
        dispatch(
          selectedFileId({
            id: Number.isFinite(numericFileId) ? numericFileId : fileId,
            name: searchParameters.get('fileName') ?? '',
            deatsilsPage: true,
          }),
        );
      }
    }, [
      dashboard?.pages,
      dispatch,
      blockDimDropdownOn,
      dataImportDropdownOn,
      formulaList,
      location.search,
      modelId,
      uiRevamp,
    ]);

    const handleDropdownSectionClick = async (
      tabKey: TabType,
      section: NavigationDropdownSection,
    ) => {
      if (tabKey === 'dashboard' && modelId && uiRevamp) {
        const page = section.source;
        saveSelectedDashboardPage(modelId, page?.id);
        dispatch(setSelectedPage(page));
        onTabChange('dashboard');
        return;
      }

      if (tabKey === 'blocks' && modelId) {
        navigate(`/model/${modelId}/block/${section.id}/plan`);
        return;
      }

      if (tabKey === 'dimensions' && modelId) {
        const response = await dispatch(FetchDimensionList(modelId));
        const selectedDimension =
          response?.payload?.dimensions?.find(
            (dimension: any) => dimension?.id === Number(section.id),
          ) || section.source;

        await dispatch(handleBuilderSelectedDimension(selectedDimension));
        onTabChange('dimensions');
        return;
      }

      if (tabKey === 'import') {
        dispatch(
          selectedFileId({
            id: section.id,
            name: section.title,
            deatsilsPage: true,
          }),
        );
        onTabChange('import');
      }
    };

    const handleDropdownSectionContextMenu = (
      event: ReactMouseEvent,
      tabKey: TabType,
      section: NavigationDropdownSection,
    ) => {
      event.preventDefault();
      event.stopPropagation();

      if (tabKey === 'dashboard' && modelId) {
        saveSelectedDashboardPage(modelId, section.id);
      }

      const url = buildDropdownSectionUrl(tabKey, section, modelId);
      if (url) {
        openUrlInNewTab(url);
      }
    };

    const renderedTabs = tabs.map(
      ({ key, label, icon: IconComponent, activeIcon: ActiveIconComponent }) => {
        const isActive = activeTab === key;
        const IconToRender = isActive ? ActiveIconComponent : IconComponent;
        const isDropdownEnabled = isDropdownEnabledForTab(key, flags);
        const dropdownSections = isDropdownEnabled
          ? buildDropdownSections(key, formulaList, dashboard?.pages, uiRevamp)
          : [];

        return (
          <NavigationFlex
            key={key}
            role='group'
            alignItems='center'
            justifyContent='center'
            gap='8px'
            // h='35px'
            px='16px'
            py='6px'
            borderRadius={radius.control}
            cursor='pointer'
            flexShrink={0}
            position='relative'
            bg='transparent'
            color='#121826'
            transition='background 0.15s ease, transform 0.1s ease'
            _hover={{ bg: isActive ? 'transparent' : 'gray.100' }}
            _active={{ bg: isActive ? 'transparent' : 'gray.200', transform: 'scale(0.97)' }}
            onClick={() => onTabChange(key)}
          >
            {isActive && (
              <MotionBox
                layoutId='activeNavTabIndicator'
                position='absolute'
                inset={0}
                bg={ACTIVE_TAB_COLOR}
                borderRadius={radius.control}
                zIndex={0}
                transition={{ type: 'spring', stiffness: 500, damping: 35 }}
              />
            )}
            <Box
              position='relative'
              zIndex={1}
              display='flex'
              alignItems='center'
              justifyContent='center'
              w='16px'
              h='16px'
            >
              <IconToRender width={16} height={16} />
            </Box>

            <Box
              as='span'
              position='relative'
              zIndex={1}
              color={isActive ? 'white' : '#121826'}
              fontSize='12px'
              fontWeight='500'
              lineHeight='1'
              whiteSpace='nowrap'
              textAlign='center'
            >
              {label}
            </Box>
            {isDropdownEnabled && (
              <NavigationBox
                position='relative'
                zIndex={1}
                display='flex'
                alignItems='center'
                justifyContent='center'
                w='10px'
                h='6px'
              >
                <NavigationArrowDownIcon isActive={isActive} />
                {dropdownSections.length > 0 && (
                  <NavigationBox
                    opacity={0}
                    visibility='hidden'
                    pointerEvents='none'
                    transition='opacity 0.15s ease, transform 0.15s ease'
                    _groupHover={{
                      opacity: 1,
                      visibility: 'visible',
                      pointerEvents: 'auto',
                      transform: 'translateX(-50%) translateY(0)',
                    }}
                    position='absolute'
                    top='5px'
                    left='50%'
                    transform='translateX(-50%) translateY(-4px)'
                    zIndex='dropdown'
                    pt='12px'
                    onClick={(event: ReactMouseEvent) => event.stopPropagation()}
                  >
                    <NavigationBox
                      bg='white'
                      border='1px solid #D1D6DD'
                      borderRadius={radius.control}
                      boxShadow='0px 8px 24px rgba(15, 23, 42, 0.16)'
                      color='#121826'
                      minW='198px'
                      maxW='288px'
                      maxH='288px'
                      overflowY='auto'
                      p='8px'
                    >
                      {dropdownSections.map((section) => (
                        <NavigationBox
                          key={section.id}
                          cursor='pointer'
                          py='4px'
                          px='4px'
                          borderRadius={radius.control}
                          _hover={DROPDOWN_ROW_HOVER_STYLE}
                          onClick={() => handleDropdownSectionClick(key, section)}
                          onContextMenu={(event: ReactMouseEvent) =>
                            handleDropdownSectionContextMenu(event, key, section)
                          }
                        >
                          <NavigationTooltip label={section.title} hasArrow placement='right'>
                            <NavigationBox
                              fontSize='13px'
                              fontWeight='400'
                              lineHeight='20px'
                              noOfLines={2}
                            >
                              {section.title}
                            </NavigationBox>
                          </NavigationTooltip>
                        </NavigationBox>
                      ))}
                    </NavigationBox>
                  </NavigationBox>
                )}
              </NavigationBox>
            )}
          </NavigationFlex>
        );
      },
    );

    return (
      <NavigationFlex
        alignItems='center'
        width='fit-content'
        bg='white'
        borderRadius={radius.control}
        flexWrap='nowrap'
        border='1px solid #D1D6DD'
        outline='1px solid #D1D6DD'
        outlineOffset='-1px'
        gap='1px'
        overflow='visible'
        p='0'
      >
        {renderedTabs}
      </NavigationFlex>
    );
  },
);

NavigationBar.displayName = 'NavigationBar';
export default withLDConsumer()(NavigationBar);
